import type { ReactNode } from 'react';
import { color, fontSize, radius } from '../../theme/tokens.js';
import { Disclosure } from '../Disclosure/Disclosure.js';

/**
 * The chart card's two readings of one response — the shape, or the figures (owner request
 * 2026-09-09).
 *
 * ## Why a pair of icons and not a `<SegmentedControl>`
 *
 * Every other control in this row is a track of words, and they all answer the same kind of
 * question: *which rows, cut how*. This one answers none of them — it changes **nothing** about
 * the data, only how the same twelve buckets are drawn. A third worded track beside Specialty and
 * Grain reads as a third filter, which is the confusion FR-034 is about; two glyphs read as a
 * view switch, which is what it is. It keeps the segmented track's chrome — same background, same
 * radius, same raised active tile — so it is recognisably the same *kind* of control, one row
 * along from the ones it is not.
 *
 * ## Why the icons are `<Disclosure>`s
 *
 * An icon-only control has no visible label, so the name has to come from somewhere: `aria-label`
 * alone serves a screen reader and leaves a sighted reader guessing, and `title` serves a pointer
 * and leaves the keyboard out. `<Disclosure>` is the product's one answer to exactly that (FR-062)
 * — hover and `:focus-visible` reveal the same panel, and the name is the accessible name — and
 * `pressed` is what tells a screen-reader user which of the two views is showing. So the two
 * glyphs are self-describing without a word of chrome on the card.
 */

export type ChartView = 'chart' | 'table';

/**
 * The two glyphs, drawn rather than pulled from an icon set: the product ships no icon
 * dependency, and the shell's own chevrons and search marks are inline `<path>`s for the same
 * reason.
 *
 * They are deliberately the two things they switch between — the chart mark is a line over its
 * sample dots, which is what the plot is (`DOT_R` markers on a monotone curve), and the table mark
 * is a header row over a grid, which is what `<DataTable>` renders. `currentColor` throughout, so
 * both follow the active/inactive ink of the tile they sit in and neither needs a colour of its
 * own in either palette.
 */
const ICON_SIZE = 16;

function LineChartIcon() {
  return (
    <svg
      width={ICON_SIZE}
      height={ICON_SIZE}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M3.5 16.5 9 10.5l3.5 3 8-9" />
      {/* The four markers, filled: without them the glyph is a tick, which is a different idea. */}
      <g fill="currentColor" stroke="none">
        <circle cx="3.5" cy="16.5" r="1.7" />
        <circle cx="9" cy="10.5" r="1.7" />
        <circle cx="12.5" cy="13.5" r="1.7" />
        <circle cx="20.5" cy="4.5" r="1.7" />
      </g>
    </svg>
  );
}

function TableIcon() {
  return (
    <svg
      width={ICON_SIZE}
      height={ICON_SIZE}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.9"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="3.25" y="4.25" width="17.5" height="15.5" rx="2.5" />
      {/* The header row, heavier than the body rules — the one thing a data table always has. */}
      <path d="M3.25 9.25h17.5" strokeWidth="2.4" />
      <path d="M3.25 14.5h17.5" />
      <path d="M9.75 9.25v10.5M15.25 9.25v10.5" />
    </svg>
  );
}

export interface ChartViewToggleProps {
  value: ChartView;
  onChange: (view: ChartView) => void;
  /**
   * How many buckets the response carries, for the panel copy. Omitted while nothing has landed
   * yet — the panel then says what each view *is* rather than naming a count it does not know.
   */
  bucketCount?: number | null;
}

export function ChartViewToggle({ value, onChange, bucketCount }: ChartViewToggleProps) {
  const buckets = bucketCount && bucketCount > 0 ? `${bucketCount} ` : '';

  return (
    <div
      role="group"
      aria-label="Chart view"
      style={{
        display: 'flex',
        gap: 2,
        background: color.surfaceTrack,
        borderRadius: radius['2xl'],
        padding: 3,
      }}
    >
      <ViewTile
        active={value === 'chart'}
        // Both names begin with the view, so the two announce as a pair rather than as two
        // unrelated buttons — and neither says "toggle", which `pressed` already carries.
        accessibleName="Chart view — the trend as a shape"
        panel={`Chart · the ${buckets}periods as lines`}
        onClick={() => onChange('chart')}
      >
        <LineChartIcon />
      </ViewTile>
      <ViewTile
        active={value === 'table'}
        accessibleName="Table view — the same figures as rows"
        panel={`Table · the same ${buckets}periods as figures`}
        onClick={() => onChange('table')}
      >
        <TableIcon />
      </ViewTile>
    </div>
  );
}

/**
 * One tile. The active one is raised on the card's own surface exactly as
 * `<SegmentedControl>`'s is — same fill, same shadow — because a reader should not have to learn
 * "this one is selected" twice on one row.
 */
function ViewTile({
  active,
  accessibleName,
  panel,
  onClick,
  children,
}: {
  active: boolean;
  accessibleName: string;
  panel: string;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <Disclosure
      accessibleName={accessibleName}
      panel={panel}
      onClick={onClick}
      pressed={active}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '4px 9px',
        borderRadius: radius.lg,
        // `.disclosure` sets `cursor: help`, which is right for a label behind a hover and wrong
        // for a control that does something — the region pills override it the same way.
        cursor: 'pointer',
        fontSize: fontSize.captionStrong,
        background: active ? color.surface : 'transparent',
        color: active ? color.ink : color.inkMuted,
        boxShadow: active ? '0 1px 2px rgba(16,35,59,.12)' : 'none',
      }}
    >
      {children}
    </Disclosure>
  );
}
